// src/components/authentication/ResetPassword.js
import React, { useEffect, useState } from "react";
import {
  getAuth,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { useSearchParams, Link } from "react-router-dom";
import { firebaseApp } from "../../firebase/firebaseApp";
import usePopup from "../../hooks/usePopup";
import ErrorPopup from "../ErrorPopup";
import { Checklist } from "./PasswordChecklist";
import usePasswordCriteria from "../../hooks/usePasswordCriteria";

/* Firebase singleton */
const auth = getAuth(firebaseApp);

export default function ResetPassword() {
  const [params] = useSearchParams();

  /* link / flow state */
  const [codeValid, setCodeValid] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  /* form state */
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showChecklist, setShowChecklist] = useState(false);
  const [viewPw, setViewPw] = useState(false);
  const [viewConfirm, setViewConfirm] = useState(false);

  const criteria = usePasswordCriteria(pw, auth);
  const { popup, open: openPopup, close: closePopup } = usePopup();

  /* ── verify oobCode on mount ─────────────────────────────── */
  useEffect(() => {
    const oob = params.get("oobCode");
    if (!oob) return;

    verifyPasswordResetCode(auth, oob)
      .then((mail) => {
        setEmail(mail);
        setCodeValid(true);
      })
      .catch(() =>
        openPopup({
          message: "Invalid or expired reset link. Please try again.",
        })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── submit new password ─────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const oob = params.get("oobCode");
    if (!oob) return;

    /* basic checks */
    if (pw !== confirm) {
      openPopup({ message: "Passwords do not match." });
      return;
    }
    if (!Object.values(criteria).every(Boolean)) {
      openPopup({ message: "Password must meet all the requirements." });
      return;
    }

    try {
      await confirmPasswordReset(auth, oob, pw);
      setDone(true);
    } catch {
      openPopup({ message: "Unable to reset password. Try again later." });
    }
  };

  /* ── render ──────────────────────────────────────────────── */
  return (
    <div className="w-full max-w-lg h-full p-4 flex flex-col justify-end md:justify-start items-center gap-4">
      {popup && <ErrorPopup popup={popup} onClose={closePopup} />}

      <img src="/pdx_logo.png" alt="pDx logo" className="h-24" />
      <h1 className="title-text">Choose a new password</h1>

      {!codeValid && <p>Validating link…</p>}

      {codeValid && !done && (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4 w-full max-w-sm"
        >
          {/* password + checklist */}
          <div className="relative flex flex-row gap-2">
            <input
              type={viewPw ? "text" : "password"}
              autoComplete="new-password"
              placeholder="New password"
              className="custom-input w-full"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              onFocus={() => setShowChecklist(true)}
              onBlur={() => setShowChecklist(false)}
              required
            />
            {showChecklist && <Checklist criteria={criteria} />}
            <button
              type="button"
              onClick={() => setViewPw((v) => !v)}
              className="input-toggle-button flex items-center !p-0"
              aria-label={viewPw ? "Hide password" : "Show password"}
              aria-pressed={viewPw}
            >
              {/*  Password visibility toggle */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className=" w-8 mx-2 input-icon"
                viewBox="0 -960 960 960"
              >
                {/* SVG Paths */}
                {viewPw ? (
                  /* eye-off path */
                  <path d="M644-428l-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428zm128 126l-58-56q38-29 67.5-63.5T832-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84-25.5t90-8.5q151 0 269 83.5T920-500q-23 59-60.5 109.5T772-302zm20 246L624-222q-35 11-70.5 16.5T480-200q-151 0-269-83.5T40-500q21-53 53-98.5t73-81.5L56-792l56-56 736 736-56 56zM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82zm319 93zm-151 75z"></path>
                ) : (
                  /* eye path */
                  <path d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200zm0-300zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280z"></path>
                )}
              </svg>
            </button>
          </div>

          {/* confirm */}
          <div className="flex flex-row gap-2">
            <input
              type={viewConfirm ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Confirm password"
              className="custom-input w-full"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setViewConfirm((v) => !v)}
              className="input-toggle-button flex items-center !p-0"
              aria-label={viewConfirm ? "Hide password" : "Show password"}
              aria-pressed={viewConfirm}
            >
              {/* Confirm Password visibility toggle */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className=" w-8 mx-2 input-icon"
                viewBox="0 -960 960 960"
              >
                {/* SVG Paths */}
                {viewConfirm ? (
                  /* eye-off path */
                  <path d="M644-428l-58-58q9-47-27-88t-93-32l-58-58q17-8 34.5-12t37.5-4q75 0 127.5 52.5T660-500q0 20-4 37.5T644-428zm128 126l-58-56q38-29 67.5-63.5T832-500q-50-101-143.5-160.5T480-720q-29 0-57 4t-55 12l-62-62q41-17 84-25.5t90-8.5q151 0 269 83.5T920-500q-23 59-60.5 109.5T772-302zm20 246L624-222q-35 11-70.5 16.5T480-200q-151 0-269-83.5T40-500q21-53 53-98.5t73-81.5L56-792l56-56 736 736-56 56zM222-624q-29 26-53 57t-41 67q50 101 143.5 160.5T480-280q20 0 39-2.5t39-5.5l-36-38q-11 3-21 4.5t-21 1.5q-75 0-127.5-52.5T300-500q0-11 1.5-21t4.5-21l-84-82zm319 93zm-151 75z"></path>
                ) : (
                  /* eye path */
                  <path d="M480-320q75 0 127.5-52.5T660-500q0-75-52.5-127.5T480-680q-75 0-127.5 52.5T300-500q0 75 52.5 127.5T480-320zm0-72q-45 0-76.5-31.5T372-500q0-45 31.5-76.5T480-608q45 0 76.5 31.5T588-500q0 45-31.5 76.5T480-392zm0 192q-146 0-266-81.5T40-500q54-137 174-218.5T480-800q146 0 266 81.5T920-500q-54 137-174 218.5T480-200zm0-300zm0 220q113 0 207.5-59.5T832-500q-50-101-144.5-160.5T480-720q-113 0-207.5 59.5T128-500q50 101 144.5 160.5T480-280z"></path>
                )}
              </svg>{" "}
            </button>
          </div>

          <button type="submit" className="primary-button disabled:opacity-50">
            Change password
          </button>
        </form>
      )}

      {done && (
        <>
          <p>
            Password updated for <strong>{email}</strong>.
          </p>
          <Link to="/auth/login" className="primary-button">
            Sign in with new password
          </Link>
        </>
      )}
    </div>
  );
}
